/**
 * One monitored account in the list's grid: avatar, name (the label if the account has one, else
 * `@handle`) on its platform, the alignment and collection-profile chips, then one line of
 * figures. The whole card opens the account through the name's stretched button; every action
 * lives in the "…" menu, lifted above it (`relative z-10`).
 */
import { useState, type ReactNode } from 'react';
import {
  Activity,
  BadgeCheck,
  Ellipsis,
  Eye,
  FileText,
  Lock,
  Pencil,
  SlidersHorizontal,
  Trash2,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { HintedIcon, IconHint } from '../../../chrome/hint';
import type { Locale } from '../../../i18n';
import { compact } from '../../../i18n/format';
import { Card } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { IconButton } from '../../../ui/icon-button';
import { AccountAvatar } from '../../data/media/AccountAvatar';
import { ALIGNMENT_TONE, DEFAULT_TAG_ICON, TagGlyph } from '../../tenant/tags/icons';
import type { Alignment, TrackedAccountRow } from './api/schemas';
import { am } from './messages';
import { platformLabel, PlatformIcon } from './NoSelection';
import { RemoveAccount } from './RemoveAccount';
import { RenameAccount } from './RenameAccount';

const ALIGNMENT_LABEL: Record<Alignment, () => string> = {
  CAMPAIGN: () => am('alignment.campaign'),
  ALLY: () => am('band.ally'),
  NEUTRAL: () => am('band.neutral'),
  ADVERSARY: () => am('band.adversary'),
};

/** The account's alignment as its tag value (the tenant's own name and icon), toned by the fixed
 *  key; an account registered before the alignment category existed falls back to the key. */
export function AlignmentChip(props: { account: TrackedAccountRow }): ReactNode {
  const { account } = props;
  if (account.alignment == null) return null;
  const tag = (account.account_tags ?? []).find(
    (each) => each.value_id === account.alignment_value_id,
  );
  return (
    <Chip
      tone={ALIGNMENT_TONE[account.alignment]}
      icon={<TagGlyph name={tag?.icon ?? DEFAULT_TAG_ICON} />}
    >
      {tag?.value ?? ALIGNMENT_LABEL[account.alignment]()}
    </Chip>
  );
}

function ago(iso: string, locale: Locale): string {
  const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60_000);
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });
  if (Math.abs(minutes) < 60) return format.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return format.format(hours, 'hour');
  return format.format(Math.round(hours / 24), 'day');
}

function monitoring(account: TrackedAccountRow, locale: Locale): { text: string; hint: string } {
  if (account.enabled_kinds.length === 0) {
    return { text: am('status.paused'), hint: am('status.paused.hint') };
  }
  if (account.last_collected_at == null) {
    return { text: am('status.waiting'), hint: am('status.waiting.hint') };
  }
  const when = ago(account.last_collected_at, locale);
  return { text: when, hint: am('card.lastCollected', { when }) };
}

function Stat(props: { icon: LucideIcon; hint: string; children: ReactNode }): ReactNode {
  const Icon = props.icon;
  return (
    <span className="flex min-w-0 items-center gap-1">
      <HintedIcon label={props.hint}>
        <Icon aria-hidden className="size-3.5" />
      </HintedIcon>
      <span className="truncate">{props.children}</span>
    </span>
  );
}

export function AccountCard(props: {
  campaignId: string;
  account: TrackedAccountRow;
  locale: Locale;
  canWrite: boolean;
  onOpen?: (accountId: string) => void;
  onEdit?: (accountId: string) => void;
}): ReactNode {
  const { account, locale, canWrite } = props;
  const [renaming, setRenaming] = useState(false);
  const [removing, setRemoving] = useState(false);
  // Label first, handle only as fallback (`api/schemas.ts`'s own §5.4 doc comment) -- a renamed
  // account with a handle still needs its label visible here, same as the detail view's own name.
  const name = account.label ?? (account.handle ? `@${account.handle}` : am('unknownHandle'));
  const open = (): void => props.onOpen?.(account.id);
  const edit = (): void => props.onEdit?.(account.id);
  const customised = account.profile_name === null;
  const status = monitoring(account, locale);

  return (
    <Card
      data-account={account.id}
      className="hover:border-primary/40 relative h-full gap-0 py-0 transition-colors"
    >
      <div className="flex items-start gap-3 px-4 pt-4 pb-3">
        <AccountAvatar
          size="md"
          avatarUrl={account.avatar_url}
          name={account.label ?? account.handle}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <h3 className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
            <HintedIcon label={platformLabel(account.platform)}>
              <PlatformIcon platform={account.platform} className="size-3.5" />
            </HintedIcon>
            <button
              type="button"
              onClick={open}
              className="focus-visible:after:ring-ring block min-w-0 truncate text-left outline-none after:absolute after:inset-0 after:rounded-xl focus-visible:after:ring-3 focus-visible:after:ring-inset"
            >
              {name}
            </button>
            {account.verified === true && (
              <HintedIcon label={am('card.verified')} className="text-primary">
                <BadgeCheck aria-hidden className="size-3.5" />
              </HintedIcon>
            )}
            {account.is_private === true && (
              <HintedIcon label={am('card.private')} className="text-muted-foreground">
                <Lock aria-hidden className="size-3" />
              </HintedIcon>
            )}
          </h3>
          {account.bio != null && account.bio !== '' && (
            <p className="text-caption text-muted-foreground line-clamp-2">{account.bio}</p>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            <AlignmentChip account={account} />
            <Chip tone="outline">
              <span className="first-letter:uppercase">
                {account.profile_name ?? am('card.customProfile')}
              </span>
              {customised && (
                <HintedIcon label={am('card.customised')}>
                  <SlidersHorizontal aria-hidden className="size-3" />
                </HintedIcon>
              )}
            </Chip>
          </div>
        </div>
        <span className="relative z-10 -me-1 -mt-1">
          <DropdownMenu>
            <IconHint label={am('card.menu')}>
              <DropdownMenuTrigger asChild>
                <IconButton label={am('card.menu')}>
                  <Ellipsis aria-hidden />
                </IconButton>
              </DropdownMenuTrigger>
            </IconHint>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={open}>
                <Eye aria-hidden />
                {am('card.open')}
              </DropdownMenuItem>
              {canWrite && (
                <>
                  <DropdownMenuItem onSelect={edit}>
                    <SlidersHorizontal aria-hidden />
                    {am('card.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setRenaming(true)}>
                    <Pencil aria-hidden />
                    {am('card.rename')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(true)}>
                    <Trash2 aria-hidden />
                    {am('card.remove')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>

      <div className="border-border text-caption text-muted-foreground mt-auto flex items-center gap-4 border-t px-4 py-2.5">
        {account.followers != null && (
          <Stat icon={Users} hint={am('card.followers')}>
            <span className="text-foreground font-semibold tabular-nums">
              {compact(account.followers, locale)}
            </span>
          </Stat>
        )}
        {account.publications != null && (
          <Stat icon={FileText} hint={am('card.related')}>
            <span className="text-foreground font-semibold tabular-nums">
              {compact(account.publications, locale)}
            </span>
          </Stat>
        )}
        {account.reach != null && (
          <Stat icon={Eye} hint={am('card.reach')}>
            <span className="text-foreground font-semibold tabular-nums">
              {compact(account.reach, locale)}
            </span>
          </Stat>
        )}
        <Stat icon={Activity} hint={status.hint}>
          {status.text}
        </Stat>
      </div>

      {renaming && (
        <RenameAccount
          campaignId={props.campaignId}
          trackedAccountId={account.id}
          label={account.label}
          handle={account.handle}
          open
          onOpenChange={setRenaming}
        />
      )}
      {removing && (
        <RemoveAccount
          campaignId={props.campaignId}
          trackedAccountId={account.id}
          name={name}
          confirming
          onConfirmingChange={setRemoving}
        />
      )}
    </Card>
  );
}
